import Phaser from 'phaser';
import {
  COLLECTIONS_CALL_MODAL,
  COLORS,
  DEPTHS,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  toHexColor,
} from '../config/gameConfig';
import { COPY } from '../content/copy';
import type { CallOutcome, EventBus } from '../systems/EventBus';
import type { CallOption, CollectionsScenario } from '../content/collections-call';
import { renderHelpLink } from './HelpLink';
import { propagateDepth } from './propagateDepth';

interface PtpValues {
  amount: number;
  date: string | null;
}

const parseAmountOption = (label: string): number => {
  if (/full/i.test(label)) return 14500;
  if (/no specific/i.test(label)) return 0;
  const match = label.match(/\$([\d,]+)/);
  if (!match) return 0;
  return Number(match[1].replace(/,/g, ''));
};

const parseDateOption = (label: string): string | null =>
  /no date set/i.test(label) ? null : label;

export class CollectionsCallModal {
  private container: Phaser.GameObjects.Container | null = null;
  private currentStep = 0;
  private inPtpForm = false;
  private currentOutcome: CallOutcome | null = null;
  private ptpSuggestion: { amount: number; date: string } | null = null;
  private amountIndex = 0;
  private dateIndex = 0;
  private escHandler: ((ev: KeyboardEvent) => void) | null = null;

  private readonly onBeat = (p: { beatId: string }) => {
    if (p.beatId === 'beat-6-promise-to-pay' && !this.container) this.open();
  };

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly bus: EventBus,
    private readonly scenario: CollectionsScenario,
  ) {
    bus.on('beat:triggered', this.onBeat);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  // Test API
  currentStepForTest(): number { return this.currentStep; }
  isInPtpFormForTest(): boolean { return this.inPtpForm; }
  chooseOptionForTest(idx: number): void {
    this.chooseOption(this.scenario.steps[this.currentStep].options[idx]);
  }
  hangupForTest(): void { this.hangup(); }
  submitPtpForTest(values: PtpValues): void { this.submitPtp(values); }

  private open(): void {
    this.currentStep = 0;
    this.inPtpForm = false;
    this.currentOutcome = null;
    this.ptpSuggestion = null;

    this.bus.emit('term:unlocked', { term: 'promise-to-pay' });
    this.bus.emit('phone:call-incoming', {
      callId: this.scenario.id,
      customer: this.scenario.customer,
      reason: this.scenario.reason,
    });
    this.bus.emit('phone:call-answered', { callId: this.scenario.id });
    this.renderDialogue();
  }

  private chooseOption(option: CallOption): void {
    if (option.outcome) {
      this.currentOutcome = option.outcome;
      this.ptpSuggestion = option.ptpSuggestion ?? null;
      if (option.outcome === 'fail') {
        this.endCallNoPtp('fail');
      } else {
        this.inPtpForm = true;
        this.renderPtpForm();
      }
    } else if (option.nextStep !== undefined) {
      this.currentStep = option.nextStep;
      this.renderDialogue();
    }
  }

  private hangup(): void {
    this.currentOutcome = 'fail';
    this.endCallNoPtp('fail');
  }

  private endCallNoPtp(outcome: CallOutcome): void {
    this.bus.emit('ptp:recorded', { customer: this.scenario.customer, amount: 0, date: null });
    this.bus.emit('phone:call-ended', { callId: this.scenario.id, outcome });
    this.close();
  }

  private submitPtp(values: PtpValues): void {
    this.bus.emit('ptp:recorded', {
      customer: this.scenario.customer,
      amount: values.amount,
      date: values.date,
    });
    this.bus.emit('phone:call-ended', {
      callId: this.scenario.id,
      outcome: this.currentOutcome ?? 'partial_success',
    });
    this.close();
  }

  private clearContainer(): void {
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
  }

  private buildBaseFrame(): { root: Phaser.GameObjects.Container; x: number; y: number } {
    this.clearContainer();
    const x = Math.round((GAME_WIDTH - COLLECTIONS_CALL_MODAL.width) / 2);
    const y = Math.round((GAME_HEIGHT - COLLECTIONS_CALL_MODAL.height) / 2);

    const root = this.scene.add.container(0, 0);
    root.setDepth(DEPTHS.modal);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, COLORS.black, COLLECTIONS_CALL_MODAL.backdropAlpha)
      .setOrigin(0, 0);
    root.add(backdrop);

    const panel = this.scene.add
      .rectangle(x, y, COLLECTIONS_CALL_MODAL.width, COLLECTIONS_CALL_MODAL.height, COLORS.windowBody)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder)
      .setInteractive();
    root.add(panel);

    // Header strip with caller info + hangup
    const headerY = y;
    const headerBg = this.scene.add
      .rectangle(x, headerY, COLLECTIONS_CALL_MODAL.width, COLLECTIONS_CALL_MODAL.headerHeight, COLORS.windowChrome)
      .setOrigin(0, 0);
    root.add(headerBg);

    const avatar = this.scene.add
      .circle(
        x + COLLECTIONS_CALL_MODAL.paddingX + COLLECTIONS_CALL_MODAL.avatarRadius,
        headerY + COLLECTIONS_CALL_MODAL.headerHeight / 2,
        COLLECTIONS_CALL_MODAL.avatarRadius,
        COLORS.buttonPrimary,
      );
    root.add(avatar);

    const nameX = x + COLLECTIONS_CALL_MODAL.paddingX + COLLECTIONS_CALL_MODAL.avatarRadius * 2 + 12;
    const nameY = headerY + 12;
    const callerName = this.scene.add.text(
      nameX,
      nameY,
      `${this.scenario.callerName} — ${this.scenario.customer}`,
      {
        fontFamily: FONTS.ui,
        fontSize: COLLECTIONS_CALL_MODAL.callerNameFontSize,
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      },
    );
    root.add(callerName);

    const subtitle = this.scene.add.text(
      nameX,
      nameY + callerName.height + 2,
      this.scenario.reason,
      {
        fontFamily: FONTS.ui,
        fontSize: COLLECTIONS_CALL_MODAL.callerSubtitleFontSize,
        color: toHexColor(COLORS.textMuted),
      },
    );
    root.add(subtitle);

    // Hangup button
    const hangupX = x + COLLECTIONS_CALL_MODAL.width - COLLECTIONS_CALL_MODAL.paddingX - COLLECTIONS_CALL_MODAL.hangupButtonWidth;
    const hangupY = headerY + (COLLECTIONS_CALL_MODAL.headerHeight - COLLECTIONS_CALL_MODAL.hangupButtonHeight) / 2;
    const hangupBg = this.scene.add
      .rectangle(hangupX, hangupY, COLLECTIONS_CALL_MODAL.hangupButtonWidth, COLLECTIONS_CALL_MODAL.hangupButtonHeight, COLORS.windowControlClose)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    root.add(hangupBg);

    const hangupLabel = this.scene.add.text(
      hangupX + COLLECTIONS_CALL_MODAL.hangupButtonWidth / 2,
      hangupY + COLLECTIONS_CALL_MODAL.hangupButtonHeight / 2,
      COPY.collectionsCall.hangupButton,
      {
        fontFamily: FONTS.ui,
        fontSize: COLLECTIONS_CALL_MODAL.hangupButtonFontSize,
        color: toHexColor(COLORS.buttonPrimaryText),
        fontStyle: 'bold',
      },
    ).setOrigin(0.5, 0.5);
    root.add(hangupLabel);

    hangupBg.on('pointerdown', () => this.hangup());

    return { root, x, y };
  }

  private renderDialogue(): void {
    const { root, x, y } = this.buildBaseFrame();
    const step = this.scenario.steps[this.currentStep];

    renderHelpLink(
      this.scene,
      root,
      x + COLLECTIONS_CALL_MODAL.width - COLLECTIONS_CALL_MODAL.paddingX,
      y + COLLECTIONS_CALL_MODAL.headerHeight + 6,
      'promise-to-pay',
      this.bus,
    );

    const customerLineY = y + COLLECTIONS_CALL_MODAL.headerHeight + COLLECTIONS_CALL_MODAL.paddingY;
    const customerLine = this.scene.add.text(
      x + COLLECTIONS_CALL_MODAL.paddingX,
      customerLineY,
      step.customerSays,
      {
        fontFamily: FONTS.ui,
        fontSize: COLLECTIONS_CALL_MODAL.customerLineFontSize,
        color: toHexColor(COLORS.textPrimary),
        wordWrap: { width: COLLECTIONS_CALL_MODAL.width - COLLECTIONS_CALL_MODAL.paddingX * 2 },
      },
    );
    root.add(customerLine);

    let optionY = customerLineY + customerLine.height + 16;
    for (const option of step.options) {
      const row = this.scene.add
        .rectangle(
          x + COLLECTIONS_CALL_MODAL.paddingX,
          optionY,
          COLLECTIONS_CALL_MODAL.width - COLLECTIONS_CALL_MODAL.paddingX * 2,
          COLLECTIONS_CALL_MODAL.optionRowHeight,
          COLORS.windowChrome,
        )
        .setOrigin(0, 0)
        .setStrokeStyle(1, COLORS.windowBorder)
        .setInteractive({ useHandCursor: true });
      root.add(row);

      const optionText = this.scene.add.text(
        x + COLLECTIONS_CALL_MODAL.paddingX + 12,
        optionY + COLLECTIONS_CALL_MODAL.optionRowHeight / 2,
        option.text,
        {
          fontFamily: FONTS.ui,
          fontSize: COLLECTIONS_CALL_MODAL.optionFontSize,
          color: toHexColor(COLORS.textPrimary),
          wordWrap: { width: COLLECTIONS_CALL_MODAL.width - COLLECTIONS_CALL_MODAL.paddingX * 2 - 24 },
        },
      ).setOrigin(0, 0.5);
      root.add(optionText);

      row.on('pointerdown', () => this.chooseOption(option));

      optionY += COLLECTIONS_CALL_MODAL.optionRowHeight + COLLECTIONS_CALL_MODAL.optionRowGap;
    }

    this.escHandler = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') this.hangup();
    };
    this.scene.input.keyboard?.on('keydown', this.escHandler);

    this.container = root;
    propagateDepth(root, DEPTHS.modal);
  }

  private renderPtpForm(): void {
    const { root, x, y } = this.buildBaseFrame();

    // Pre-fill indices from suggestion
    if (this.ptpSuggestion) {
      const matchAmount = COPY.collectionsCall.ptpAmountOptions.findIndex(
        (label) => parseAmountOption(label) === this.ptpSuggestion!.amount,
      );
      this.amountIndex = matchAmount >= 0 ? matchAmount : 0;
      const matchDate = COPY.collectionsCall.ptpDateOptions.findIndex(
        (label) => label === this.ptpSuggestion!.date,
      );
      this.dateIndex = matchDate >= 0 ? matchDate : 0;
    } else {
      this.amountIndex = 0;
      this.dateIndex = 0;
    }

    const titleY = y + COLLECTIONS_CALL_MODAL.headerHeight + COLLECTIONS_CALL_MODAL.paddingY;
    const title = this.scene.add.text(
      x + COLLECTIONS_CALL_MODAL.paddingX,
      titleY,
      COPY.collectionsCall.ptpFormTitle,
      {
        fontFamily: FONTS.ui,
        fontSize: COLLECTIONS_CALL_MODAL.ptpFormTitleFontSize,
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      },
    );
    root.add(title);

    // Amount field
    const amountLabelY = titleY + title.height + 16;
    const amountLabel = this.scene.add.text(
      x + COLLECTIONS_CALL_MODAL.paddingX,
      amountLabelY,
      COPY.collectionsCall.ptpAmountLabel,
      {
        fontFamily: FONTS.ui,
        fontSize: COLLECTIONS_CALL_MODAL.ptpFieldLabelFontSize,
        color: toHexColor(COLORS.textMuted),
      },
    );
    root.add(amountLabel);

    const amountFieldY = amountLabelY + amountLabel.height + 6;
    const amountFieldWidth = COLLECTIONS_CALL_MODAL.width - COLLECTIONS_CALL_MODAL.paddingX * 2;
    const amountField = this.scene.add
      .rectangle(
        x + COLLECTIONS_CALL_MODAL.paddingX,
        amountFieldY,
        amountFieldWidth,
        COLLECTIONS_CALL_MODAL.ptpFieldHeight,
        COLORS.windowChrome,
      )
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder)
      .setInteractive({ useHandCursor: true });
    root.add(amountField);

    const amountText = this.scene.add.text(
      x + COLLECTIONS_CALL_MODAL.paddingX + 12,
      amountFieldY + COLLECTIONS_CALL_MODAL.ptpFieldHeight / 2,
      COPY.collectionsCall.ptpAmountOptions[this.amountIndex],
      {
        fontFamily: FONTS.ui,
        fontSize: COLLECTIONS_CALL_MODAL.ptpFieldFontSize,
        color: toHexColor(COLORS.textPrimary),
      },
    ).setOrigin(0, 0.5);
    root.add(amountText);

    amountField.on('pointerdown', () => {
      this.amountIndex = (this.amountIndex + 1) % COPY.collectionsCall.ptpAmountOptions.length;
      amountText.setText(COPY.collectionsCall.ptpAmountOptions[this.amountIndex]);
    });

    // Date field
    const dateLabelY = amountFieldY + COLLECTIONS_CALL_MODAL.ptpFieldHeight + COLLECTIONS_CALL_MODAL.ptpFieldGap;
    const dateLabel = this.scene.add.text(
      x + COLLECTIONS_CALL_MODAL.paddingX,
      dateLabelY,
      COPY.collectionsCall.ptpDateLabel,
      {
        fontFamily: FONTS.ui,
        fontSize: COLLECTIONS_CALL_MODAL.ptpFieldLabelFontSize,
        color: toHexColor(COLORS.textMuted),
      },
    );
    root.add(dateLabel);

    const dateFieldY = dateLabelY + dateLabel.height + 6;
    const dateField = this.scene.add
      .rectangle(
        x + COLLECTIONS_CALL_MODAL.paddingX,
        dateFieldY,
        amountFieldWidth,
        COLLECTIONS_CALL_MODAL.ptpFieldHeight,
        COLORS.windowChrome,
      )
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder)
      .setInteractive({ useHandCursor: true });
    root.add(dateField);

    const dateText = this.scene.add.text(
      x + COLLECTIONS_CALL_MODAL.paddingX + 12,
      dateFieldY + COLLECTIONS_CALL_MODAL.ptpFieldHeight / 2,
      COPY.collectionsCall.ptpDateOptions[this.dateIndex],
      {
        fontFamily: FONTS.ui,
        fontSize: COLLECTIONS_CALL_MODAL.ptpFieldFontSize,
        color: toHexColor(COLORS.textPrimary),
      },
    ).setOrigin(0, 0.5);
    root.add(dateText);

    dateField.on('pointerdown', () => {
      this.dateIndex = (this.dateIndex + 1) % COPY.collectionsCall.ptpDateOptions.length;
      dateText.setText(COPY.collectionsCall.ptpDateOptions[this.dateIndex]);
    });

    // Submit button
    const submitY = y + COLLECTIONS_CALL_MODAL.height - COLLECTIONS_CALL_MODAL.paddingY - COLLECTIONS_CALL_MODAL.ptpSubmitButtonHeight;
    const submitButton = this.scene.add
      .rectangle(
        x + COLLECTIONS_CALL_MODAL.paddingX,
        submitY,
        amountFieldWidth,
        COLLECTIONS_CALL_MODAL.ptpSubmitButtonHeight,
        COLORS.buttonPrimary,
      )
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    root.add(submitButton);

    const submitLabel = this.scene.add.text(
      x + COLLECTIONS_CALL_MODAL.width / 2,
      submitY + COLLECTIONS_CALL_MODAL.ptpSubmitButtonHeight / 2,
      COPY.collectionsCall.ptpSubmitButton,
      {
        fontFamily: FONTS.ui,
        fontSize: COLLECTIONS_CALL_MODAL.ptpSubmitButtonFontSize,
        color: toHexColor(COLORS.buttonPrimaryText),
        fontStyle: 'bold',
      },
    ).setOrigin(0.5, 0.5);
    root.add(submitLabel);

    submitButton.on('pointerdown', () => {
      const amount = parseAmountOption(COPY.collectionsCall.ptpAmountOptions[this.amountIndex]);
      const date = parseDateOption(COPY.collectionsCall.ptpDateOptions[this.dateIndex]);
      this.submitPtp({ amount, date });
    });

    // Skip text button
    const skip = this.scene.add
      .text(
        x + COLLECTIONS_CALL_MODAL.width - COLLECTIONS_CALL_MODAL.paddingX,
        submitY - 12,
        COPY.collectionsCall.ptpSkipButton,
        {
          fontFamily: FONTS.ui,
          fontSize: COLLECTIONS_CALL_MODAL.ptpSkipButtonFontSize,
          color: toHexColor(COLORS.textMuted),
        },
      )
      .setOrigin(1, 1)
      .setInteractive({ useHandCursor: true });
    skip.on('pointerdown', () => this.submitPtp({ amount: 0, date: null }));
    root.add(skip);

    this.container = root;
    propagateDepth(root, DEPTHS.modal);
  }

  private close(): void {
    if (this.escHandler) {
      this.scene.input.keyboard?.off('keydown', this.escHandler);
      this.escHandler = null;
    }
    this.clearContainer();
  }

  private teardown(): void {
    this.bus.off('beat:triggered', this.onBeat);
    this.close();
  }
}
